/**
 * Admin Folders screen — view all folders, manage member access and roles.
 */
import { folderService, userService, worksheetService } from '../services/api';
import { sanitize, getStatusDef } from '../services/utils';

let _container: HTMLElement;
let _folders: any[] = [];
let _selectedFolder: any = null;
let _members: any[] = [];
let _worksheets: any[] = [];
let _allUsers: any[] = [];

export async function renderFolderMgmt(container: HTMLElement, selectFolderId?: string): Promise<void> {
  _container = container;
  await loadData();

  // Auto-select folder if ID provided (deep link from user view)
  if (selectFolderId) {
    _selectedFolder = _folders.find(f => f._id === selectFolderId) || null;
    if (_selectedFolder) {
      await Promise.all([loadMembers(_selectedFolder._id), loadWorksheets(_selectedFolder._id), loadAllUsers()]);
    }
  }

  render();
}

async function loadData(): Promise<void> {
  try {
    const { folders } = await folderService.list();
    _folders = folders;
  } catch { _folders = []; }
}

async function loadMembers(folderId: string): Promise<void> {
  try {
    const { members } = await folderService.listMembers(folderId);
    _members = members;
  } catch { _members = []; }
}

async function loadWorksheets(folderId: string): Promise<void> {
  try {
    const { worksheets } = await worksheetService.list(folderId);
    _worksheets = worksheets;
  } catch { _worksheets = []; }
}

async function loadAllUsers(): Promise<void> {
  if (_allUsers.length > 0) return;
  try {
    const { users } = await userService.list();
    _allUsers = users;
  } catch { _allUsers = []; }
}

function render(): void {
  _container.innerHTML = `
    <div style="display:flex;gap:16px;min-height:400px">
      <div style="width:280px;flex-shrink:0;border:1px solid var(--colorBorder);border-radius:var(--radiusLg);background:var(--colorBgCard);overflow:hidden">
        <div style="padding:10px 12px;border-bottom:1px solid var(--colorBorder);font-size:10px;text-transform:uppercase;letter-spacing:.1em;color:var(--colorTextLight)">All Folders (${_folders.length})</div>
        <div id="folder-list" style="overflow-y:auto;max-height:500px">
          ${_folders.map(f => {
            const isSelected = _selectedFolder?._id === f._id;
            return `<div data-folder-id="${f._id}" style="padding:8px 12px;cursor:pointer;border-left:3px solid ${isSelected ? 'var(--colorAccent)' : 'transparent'};background:${isSelected ? 'var(--colorBgElevated)' : 'none'};transition:all .1s" onmouseover="if(!this.style.borderLeftColor.includes('colorAccent'))this.style.background='var(--colorBgElevated)'" onmouseout="if(!this.style.borderLeftColor.includes('colorAccent'))this.style.background='none'">
              <div style="font-size:12px;font-weight:500;color:var(--colorTextHeading)">${sanitize(f.name)}</div>
              <div style="font-size:10px;color:var(--colorTextMuted);display:flex;gap:8px;margin-top:2px">
                <span>${f.worksheet_count || 0} worksheets</span>
                ${f.ref_id ? `<span>Ref: ${sanitize(f.ref_id)}</span>` : ''}
              </div>
            </div>`;
          }).join('')}
          ${_folders.length === 0 ? '<div style="padding:16px;text-align:center;font-size:11px;color:var(--colorTextLight)">No folders</div>' : ''}
        </div>
      </div>
      <div style="flex:1;border:1px solid var(--colorBorder);border-radius:var(--radiusLg);background:var(--colorBgCard);overflow:hidden">
        ${_selectedFolder ? renderFolderDetail() : '<div style="display:flex;align-items:center;justify-content:center;height:100%;font-size:12px;color:var(--colorTextLight)">Select a folder to manage access</div>'}
      </div>
    </div>
  `;

  // Folder click handlers
  _container.querySelectorAll('[data-folder-id]').forEach(el => {
    el.addEventListener('click', async () => {
      const id = (el as HTMLElement).dataset.folderId!;
      _selectedFolder = _folders.find(f => f._id === id);
      if (_selectedFolder) {
        await Promise.all([loadMembers(id), loadWorksheets(id), loadAllUsers()]);
      }
      render();
    });
  });

  // Member action handlers
  _container.querySelectorAll('[data-change-role]').forEach(el => {
    el.addEventListener('change', async (e) => {
      const userId = (el as HTMLElement).dataset.changeRole!;
      const role = (e.target as HTMLSelectElement).value;
      try {
        await folderService.updateMemberRole(_selectedFolder._id, userId, role as 'editor' | 'viewer');
        await loadMembers(_selectedFolder._id);
        render();
      } catch (err: any) {
        alert(err.message || 'Failed to update role');
      }
    });
  });

  _container.querySelectorAll('[data-remove-member]').forEach(el => {
    el.addEventListener('click', async () => {
      const userId = (el as HTMLElement).dataset.removeMember!;
      const member = _members.find(m => m.user_id === userId);
      if (!confirm(`Remove ${member?.display_name || member?.email || 'this user'} from the folder?`)) return;
      try {
        await folderService.removeMember(_selectedFolder._id, userId);
        await loadMembers(_selectedFolder._id);
        render();
      } catch (err: any) {
        alert(err.message || 'Failed to remove member');
      }
    });
  });

  // Add member
  document.getElementById('add-member-btn')?.addEventListener('click', () => showAddMemberModal());

  // Export worksheet JSON
  _container.querySelectorAll('[data-export-ws]').forEach(el => {
    el.addEventListener('click', async () => {
      const wsId = (el as HTMLElement).dataset.exportWs!;
      try {
        const data = await worksheetService.export(_selectedFolder._id, wsId);
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const ws = _worksheets.find(w => w._id === wsId);
        a.download = `${(ws?.name || 'worksheet').replace(/\s+/g, '-').toLowerCase()}.json`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (err: any) {
        alert(err.message || 'Export failed');
      }
    });
  });

  // Clone worksheet
  _container.querySelectorAll('[data-clone-ws]').forEach(el => {
    el.addEventListener('click', async () => {
      const wsId = (el as HTMLElement).dataset.cloneWs!;
      const ws = _worksheets.find(w => w._id === wsId);
      if (!confirm(`Clone "${ws?.name || 'Untitled'}"?`)) return;
      try {
        await worksheetService.duplicate(_selectedFolder._id, wsId);
        await loadWorksheets(_selectedFolder._id);
        render();
      } catch (err: any) {
        alert(err.message || 'Clone failed');
      }
    });
  });

  // Move worksheet
  _container.querySelectorAll('[data-move-ws]').forEach(el => {
    el.addEventListener('click', () => {
      const wsId = (el as HTMLElement).dataset.moveWs!;
      const ws = _worksheets.find(w => w._id === wsId);
      showFolderPickerModal(wsId, ws?.name || 'Untitled', 'move');
    });
  });

  // Copy worksheet
  _container.querySelectorAll('[data-copy-ws]').forEach(el => {
    el.addEventListener('click', () => {
      const wsId = (el as HTMLElement).dataset.copyWs!;
      const ws = _worksheets.find(w => w._id === wsId);
      showFolderPickerModal(wsId, ws?.name || 'Untitled', 'copy');
    });
  });

  // Symlink worksheet
  _container.querySelectorAll('[data-symlink-ws]').forEach(el => {
    el.addEventListener('click', () => {
      const wsId = (el as HTMLElement).dataset.symlinkWs!;
      const ws = _worksheets.find(w => w._id === wsId);
      showFolderPickerModal(wsId, ws?.name || 'Untitled', 'symlink');
    });
  });

  // Lock/Unlock
  _container.querySelectorAll('[data-lock-ws]').forEach(el => {
    el.addEventListener('click', async () => {
      const wsId = (el as HTMLElement).dataset.lockWs!;
      const isLocked = (el as HTMLElement).dataset.locked === '1';
      try {
        await worksheetService.setReadOnly(_selectedFolder._id, wsId, !isLocked);
        await loadWorksheets(_selectedFolder._id);
        render();
      } catch (err: any) { alert(err.message || 'Failed'); }
    });
  });

  // Tags
  _container.querySelectorAll('[data-tags-ws]').forEach(el => {
    el.addEventListener('click', () => {
      const wsId = (el as HTMLElement).dataset.tagsWs!;
      const current = ((el as HTMLElement).dataset.wsTags || '').split(',').filter(Boolean);
      showTagsModal(wsId, current);
    });
  });

  // Permissions
  _container.querySelectorAll('[data-perms-ws]').forEach(el => {
    el.addEventListener('click', () => {
      const wsId = (el as HTMLElement).dataset.permsWs!;
      showPermissionsModal(wsId);
    });
  });
}

function showFolderPickerModal(wsId: string, wsName: string, action: 'move' | 'copy' | 'symlink'): void {
  const labels = { move: 'Move', copy: 'Copy', symlink: 'Create Shortcut' };
  const otherFolders = action === 'move' ? _folders.filter(f => f._id !== _selectedFolder._id) : _folders;

  const overlay = document.createElement('div');
  overlay.style.cssText = 'position:fixed;inset:0;z-index:2000;background:rgba(0,0,0,.45);display:flex;align-items:center;justify-content:center';
  overlay.onclick = e => { if (e.target === overlay) overlay.remove(); };

  const modal = document.createElement('div');
  modal.style.cssText = 'background:var(--colorBgCard);border:1px solid var(--colorBorder);border-radius:12px;padding:20px;box-shadow:0 16px 48px rgba(0,0,0,.2);max-width:360px;width:90%';
  modal.innerHTML = `
    <div style="font-size:14px;font-weight:600;color:var(--colorTextHeading);margin-bottom:4px">${labels[action]} Worksheet</div>
    <p style="font-size:11px;color:var(--colorTextMuted);margin-bottom:12px">${labels[action]} <strong>${sanitize(wsName)}</strong> to a folder.</p>
    <div style="max-height:250px;overflow-y:auto">
      ${otherFolders.map(f => `
        <div data-pick-folder="${f._id}" style="display:flex;align-items:center;gap:8px;padding:8px 10px;border-radius:6px;cursor:pointer" onmouseover="this.style.background='var(--colorBgElevated)'" onmouseout="this.style.background='none'">
          <svg width="14" height="14" fill="none" stroke="var(--colorTextLight)" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/></svg>
          <span style="font-size:12px;color:var(--colorText)">${sanitize(f.name)}</span>
          <span style="font-size:9px;color:var(--colorTextLight);margin-left:auto">${f.worksheet_count || 0} ws</span>
        </div>
      `).join('')}
      ${otherFolders.length === 0 ? '<div style="font-size:11px;color:var(--colorTextLight);text-align:center;padding:16px">No folders available</div>' : ''}
    </div>
  `;

  overlay.appendChild(modal);
  document.body.appendChild(overlay);

  modal.querySelectorAll('[data-pick-folder]').forEach(el => {
    el.addEventListener('click', async () => {
      const targetId = (el as HTMLElement).dataset.pickFolder!;
      try {
        if (action === 'move') await worksheetService.move(_selectedFolder._id, wsId, targetId);
        else if (action === 'copy') await worksheetService.copy(_selectedFolder._id, wsId, targetId);
        else if (action === 'symlink') await worksheetService.createSymlink(_selectedFolder._id, wsId, targetId);
        overlay.remove();
        await Promise.all([loadWorksheets(_selectedFolder._id), loadData()]);
        render();
      } catch (err: any) { alert(err.message || `${labels[action]} failed`); }
    });
  });
}

function showTagsModal(wsId: string, currentTags: string[]): void {
  const overlay = document.createElement('div');
  overlay.style.cssText = 'position:fixed;inset:0;z-index:2000;background:rgba(0,0,0,.45);display:flex;align-items:center;justify-content:center';
  overlay.onclick = e => { if (e.target === overlay) overlay.remove(); };

  const modal = document.createElement('div');
  modal.style.cssText = 'background:var(--colorBgCard);border:1px solid var(--colorBorder);border-radius:12px;padding:20px;box-shadow:0 16px 48px rgba(0,0,0,.2);max-width:360px;width:90%';
  modal.innerHTML = `
    <div style="font-size:14px;font-weight:600;color:var(--colorTextHeading);margin-bottom:8px">Edit Tags</div>
    <p style="font-size:11px;color:var(--colorTextMuted);margin-bottom:8px">Comma-separated tags for categorization.</p>
    <input id="tags-input" type="text" value="${currentTags.join(', ')}" placeholder="e.g. priority, review-needed, q2" style="width:100%;padding:8px 10px;border:1px solid var(--colorBorder);border-radius:6px;font-size:12px;background:var(--colorBgElevated);color:var(--colorText);outline:none;box-sizing:border-box">
    <div style="display:flex;gap:8px;margin-top:12px;justify-content:flex-end">
      <button id="tags-cancel" style="padding:6px 14px;border-radius:6px;font-size:11px;cursor:pointer;border:1px solid var(--colorBorder);background:var(--colorBgElevated);color:var(--colorText)">Cancel</button>
      <button id="tags-save" style="padding:6px 14px;border-radius:6px;font-size:11px;cursor:pointer;border:none;background:var(--colorPrimary);color:#fff">Save</button>
    </div>
  `;
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
  (document.getElementById('tags-input') as HTMLInputElement)?.focus();
  document.getElementById('tags-cancel')!.onclick = () => overlay.remove();
  document.getElementById('tags-save')!.onclick = async () => {
    const raw = (document.getElementById('tags-input') as HTMLInputElement).value;
    const tags = raw.split(',').map(t => t.trim()).filter(Boolean);
    try {
      await worksheetService.updateTags(_selectedFolder._id, wsId, tags);
      overlay.remove();
      await loadWorksheets(_selectedFolder._id);
      render();
    } catch (err: any) { alert(err.message || 'Failed to update tags'); }
  };
}

async function showPermissionsModal(wsId: string): Promise<void> {
  await loadAllUsers();
  const ws = _worksheets.find(w => w._id === wsId);
  const currentPerms: any[] = ws?.permissions || [];

  const overlay = document.createElement('div');
  overlay.style.cssText = 'position:fixed;inset:0;z-index:2000;background:rgba(0,0,0,.45);display:flex;align-items:center;justify-content:center';
  overlay.onclick = e => { if (e.target === overlay) overlay.remove(); };

  const modal = document.createElement('div');
  modal.style.cssText = 'background:var(--colorBgCard);border:1px solid var(--colorBorder);border-radius:12px;padding:20px;box-shadow:0 16px 48px rgba(0,0,0,.2);max-width:440px;width:90%';

  function renderPermsContent(): void {
    modal.innerHTML = `
      <div style="font-size:14px;font-weight:600;color:var(--colorTextHeading);margin-bottom:4px">Worksheet Permissions</div>
      <p style="font-size:11px;color:var(--colorTextMuted);margin-bottom:12px">Override folder-level access for specific users. Leave empty to use folder defaults.</p>
      <div style="max-height:250px;overflow-y:auto;margin-bottom:12px">
        ${currentPerms.map((p, i) => {
          const user = _allUsers.find(u => u._id === (p.user_id?._id || p.user_id));
          return `<div style="display:flex;align-items:center;gap:8px;padding:4px 0;border-bottom:1px solid var(--colorBorder)">
            <span style="flex:1;font-size:11px;color:var(--colorText)">${sanitize(user?.display_name || user?.email || p.user_id)}</span>
            <select data-perm-idx="${i}" style="font-size:10px;padding:2px 6px;border-radius:4px;border:1px solid var(--colorBorder);background:var(--colorBgElevated);color:var(--colorText)">
              <option value="editor" ${p.role === 'editor' ? 'selected' : ''}>Editor</option>
              <option value="viewer" ${p.role === 'viewer' ? 'selected' : ''}>Viewer</option>
              <option value="none" ${p.role === 'none' ? 'selected' : ''}>Denied</option>
            </select>
            <button data-remove-perm="${i}" style="background:none;border:none;cursor:pointer;color:var(--colorTextLight);padding:2px;line-height:0" title="Remove"><svg width="10" height="10" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg></button>
          </div>`;
        }).join('')}
        ${currentPerms.length === 0 ? '<div style="font-size:11px;color:var(--colorTextLight);padding:8px 0">No overrides — using folder defaults</div>' : ''}
      </div>
      <div style="display:flex;gap:8px;align-items:center;margin-bottom:12px">
        <select id="perm-add-user" style="flex:1;font-size:10px;padding:4px 8px;border-radius:4px;border:1px solid var(--colorBorder);background:var(--colorBgElevated);color:var(--colorText)">
          <option value="">Add user...</option>
          ${_allUsers.filter(u => !currentPerms.find(p => (p.user_id?._id || p.user_id) === u._id)).map(u =>
            `<option value="${u._id}">${sanitize(u.display_name || u.email)}</option>`
          ).join('')}
        </select>
        <button id="perm-add-btn" style="padding:4px 10px;border-radius:4px;font-size:10px;cursor:pointer;border:1px solid var(--colorBorder);background:var(--colorBgElevated);color:var(--colorText)">Add</button>
      </div>
      <div style="display:flex;gap:8px;justify-content:flex-end">
        <button id="perm-cancel" style="padding:6px 14px;border-radius:6px;font-size:11px;cursor:pointer;border:1px solid var(--colorBorder);background:var(--colorBgElevated);color:var(--colorText)">Cancel</button>
        <button id="perm-save" style="padding:6px 14px;border-radius:6px;font-size:11px;cursor:pointer;border:none;background:var(--colorPrimary);color:#fff">Save</button>
      </div>
    `;

    // Wire events
    modal.querySelectorAll('[data-perm-idx]').forEach(sel => {
      sel.addEventListener('change', (e) => {
        const idx = parseInt((sel as HTMLElement).dataset.permIdx!, 10);
        currentPerms[idx].role = (e.target as HTMLSelectElement).value;
      });
    });
    modal.querySelectorAll('[data-remove-perm]').forEach(btn => {
      btn.addEventListener('click', () => {
        const idx = parseInt((btn as HTMLElement).dataset.removePerm!, 10);
        currentPerms.splice(idx, 1);
        renderPermsContent();
      });
    });
    document.getElementById('perm-add-btn')!.onclick = () => {
      const sel = document.getElementById('perm-add-user') as HTMLSelectElement;
      if (!sel.value) return;
      currentPerms.push({ user_id: sel.value, role: 'viewer' });
      renderPermsContent();
    };
    document.getElementById('perm-cancel')!.onclick = () => overlay.remove();
    document.getElementById('perm-save')!.onclick = async () => {
      try {
        await worksheetService.updatePermissions(_selectedFolder._id, wsId, currentPerms);
        overlay.remove();
        await loadWorksheets(_selectedFolder._id);
        render();
      } catch (err: any) { alert(err.message || 'Failed to save permissions'); }
    };
  }

  overlay.appendChild(modal);
  document.body.appendChild(overlay);
  renderPermsContent();
}

function renderFolderDetail(): string {
  const f = _selectedFolder;
  const ownerMember = _members.find(m => m.is_owner);

  return `
    <div style="padding:12px 16px;border-bottom:1px solid var(--colorBorder);display:flex;align-items:center;justify-content:space-between">
      <div>
        <div style="font-size:14px;font-weight:600;color:var(--colorTextHeading)">${sanitize(f.name)}</div>
        <div style="font-size:10px;color:var(--colorTextMuted)">${f.ref_id ? 'Ref: ' + sanitize(f.ref_id) + ' · ' : ''}${f.worksheet_count || 0} worksheets · Created ${new Date(f.created_at).toLocaleDateString()}</div>
      </div>
      <button id="add-member-btn" style="padding:5px 12px;border-radius:6px;font-size:11px;cursor:pointer;border:1px solid var(--colorBorder);background:var(--colorBgElevated);color:var(--colorText)">+ Add Member</button>
    </div>
    <div style="padding:12px 16px">
      <div style="font-size:10px;text-transform:uppercase;letter-spacing:.1em;color:var(--colorTextLight);margin-bottom:8px">Members (${_members.length})</div>
      ${_members.map(m => {
        const roleColors: Record<string, string> = { owner: 'var(--colorAccent)', editor: '#10b981', viewer: '#94a3b8' };
        return `<div style="display:flex;align-items:center;gap:10px;padding:6px 0;border-bottom:1px solid var(--colorBorder)">
          <div style="flex:1;min-width:0">
            <div style="font-size:12px;font-weight:500;color:var(--colorTextHeading)">${sanitize(m.display_name || m.email)}</div>
            <div style="font-size:10px;color:var(--colorTextMuted)">${sanitize(m.email)}</div>
          </div>
          ${m.is_owner ? `<span style="font-size:9px;font-weight:600;color:var(--colorAccent);text-transform:uppercase;letter-spacing:.05em">Owner</span>` : `
            <select data-change-role="${m.user_id}" style="font-size:10px;padding:2px 6px;border-radius:4px;border:1px solid var(--colorBorder);background:var(--colorBgElevated);color:${roleColors[m.role] || 'var(--colorText)'};cursor:pointer">
              <option value="editor" ${m.role === 'editor' ? 'selected' : ''}>Editor</option>
              <option value="viewer" ${m.role === 'viewer' ? 'selected' : ''}>Viewer</option>
            </select>
            <button data-remove-member="${m.user_id}" style="background:none;border:none;cursor:pointer;color:var(--colorTextLight);padding:2px;line-height:0;opacity:.5" onmouseover="this.style.opacity='1';this.style.color='var(--colorError)'" onmouseout="this.style.opacity='.5';this.style.color='var(--colorTextLight)'" title="Remove member">
              <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
            </button>
          `}
        </div>`;
      }).join('')}
      ${_members.length === 0 ? '<div style="font-size:11px;color:var(--colorTextLight);padding:12px 0">No members</div>' : ''}
    </div>
    <div style="padding:12px 16px;border-top:1px solid var(--colorBorder)">
      <div style="font-size:10px;text-transform:uppercase;letter-spacing:.1em;color:var(--colorTextLight);margin-bottom:8px">Worksheets (${_worksheets.length})</div>
      ${_worksheets.length > 0 ? _worksheets.map(w => {
        const sd = getStatusDef(w.status);
        return `<div style="display:flex;align-items:center;gap:10px;padding:5px 0;border-bottom:1px solid var(--colorBorder)">
          <span style="display:inline-block;padding:1px 6px;border-radius:4px;font-size:9px;font-weight:500;background:${sd.bg};color:${sd.color}">${sd.label}</span>
          ${w.read_only ? '<span title="Locked (read-only)" style="color:var(--colorTextLight);line-height:0"><svg width="10" height="10" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/></svg></span>' : ''}
          ${w.is_symlink ? '<span title="Shortcut" style="color:var(--colorAccent);line-height:0"><svg width="10" height="10" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/></svg></span>' : ''}
          <span style="flex:1;font-size:12px;color:var(--colorText);min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${sanitize(w.name || 'Untitled')}</span>
          ${(w.tags || []).map((t: string) => `<span style="font-size:8px;padding:0 4px;border-radius:3px;background:var(--colorBgElevated);color:var(--colorTextMuted)">${sanitize(t)}</span>`).join('')}
          ${w.template_abbr ? `<span style="font-size:9px;padding:1px 4px;border-radius:3px;background:var(--colorBgElevated);color:var(--colorTextMuted)">${sanitize(w.template_abbr)}</span>` : ''}
          <span style="font-size:9px;color:var(--colorTextLight)">${w.actor_count || 0} actors</span>
          <a href="/worksheet?server=1&folderId=${f._id}&worksheetId=${w._id}&screen=output" style="font-size:9px;color:var(--colorAccent);text-decoration:none;padding:1px 5px;border:1px solid var(--colorBorder);border-radius:4px" onmouseover="this.style.borderColor='var(--colorAccent)'" onmouseout="this.style.borderColor='var(--colorBorder)'" title="View results">Results</a>
          <button data-export-ws="${w._id}" style="font-size:9px;color:var(--colorTextLight);background:none;border:1px solid var(--colorBorder);border-radius:4px;padding:1px 5px;cursor:pointer" onmouseover="this.style.borderColor='var(--colorAccent)';this.style.color='var(--colorAccent)'" onmouseout="this.style.borderColor='var(--colorBorder)';this.style.color='var(--colorTextLight)'" title="Export JSON">Export</button>
          <button data-clone-ws="${w._id}" style="font-size:9px;color:var(--colorTextLight);background:none;border:1px solid var(--colorBorder);border-radius:4px;padding:1px 5px;cursor:pointer" onmouseover="this.style.borderColor='var(--colorAccent)';this.style.color='var(--colorAccent)'" onmouseout="this.style.borderColor='var(--colorBorder)';this.style.color='var(--colorTextLight)'" title="Duplicate">Clone</button>
          <button data-copy-ws="${w._id}" style="font-size:9px;color:var(--colorTextLight);background:none;border:1px solid var(--colorBorder);border-radius:4px;padding:1px 5px;cursor:pointer" onmouseover="this.style.borderColor='var(--colorAccent)';this.style.color='var(--colorAccent)'" onmouseout="this.style.borderColor='var(--colorBorder)';this.style.color='var(--colorTextLight)'" title="Copy to another folder">Copy</button>
          <button data-move-ws="${w._id}" style="font-size:9px;color:var(--colorTextLight);background:none;border:1px solid var(--colorBorder);border-radius:4px;padding:1px 5px;cursor:pointer" onmouseover="this.style.borderColor='var(--colorAccent)';this.style.color='var(--colorAccent)'" onmouseout="this.style.borderColor='var(--colorBorder)';this.style.color='var(--colorTextLight)'" title="Move to another folder">Move</button>
          <button data-symlink-ws="${w._id}" style="font-size:9px;color:var(--colorTextLight);background:none;border:1px solid var(--colorBorder);border-radius:4px;padding:1px 5px;cursor:pointer" onmouseover="this.style.borderColor='var(--colorAccent)';this.style.color='var(--colorAccent)'" onmouseout="this.style.borderColor='var(--colorBorder)';this.style.color='var(--colorTextLight)'" title="Create shortcut in another folder">Link</button>
          <button data-lock-ws="${w._id}" data-locked="${w.read_only ? '1' : '0'}" style="font-size:9px;color:${w.read_only ? 'var(--colorAccent)' : 'var(--colorTextLight)'};background:none;border:1px solid var(--colorBorder);border-radius:4px;padding:1px 5px;cursor:pointer" title="${w.read_only ? 'Unlock' : 'Lock'}">${w.read_only ? 'Unlock' : 'Lock'}</button>
          <button data-tags-ws="${w._id}" data-ws-tags="${(w.tags || []).join(',')}" style="font-size:9px;color:var(--colorTextLight);background:none;border:1px solid var(--colorBorder);border-radius:4px;padding:1px 5px;cursor:pointer" onmouseover="this.style.borderColor='var(--colorAccent)';this.style.color='var(--colorAccent)'" onmouseout="this.style.borderColor='var(--colorBorder)';this.style.color='var(--colorTextLight)'" title="Edit tags">Tags</button>
          <button data-perms-ws="${w._id}" style="font-size:9px;color:var(--colorTextLight);background:none;border:1px solid var(--colorBorder);border-radius:4px;padding:1px 5px;cursor:pointer" onmouseover="this.style.borderColor='var(--colorAccent)';this.style.color='var(--colorAccent)'" onmouseout="this.style.borderColor='var(--colorBorder)';this.style.color='var(--colorTextLight)'" title="Manage permissions">Perms</button>
        </div>`;
      }).join('') : '<div style="font-size:11px;color:var(--colorTextLight);padding:12px 0">No worksheets in this folder</div>'}
    </div>
  `;
}

function showAddMemberModal(): void {
  // Filter out users already in the folder
  const memberIds = new Set(_members.map(m => m.user_id));
  const available = _allUsers.filter(u => !memberIds.has(u._id));

  const overlay = document.createElement('div');
  overlay.id = 'add-member-overlay';
  overlay.style.cssText = 'position:fixed;inset:0;z-index:2000;background:rgba(0,0,0,.45);display:flex;align-items:center;justify-content:center';
  overlay.onclick = e => { if (e.target === overlay) overlay.remove(); };

  const modal = document.createElement('div');
  modal.style.cssText = 'background:var(--colorBgCard);border:1px solid var(--colorBorder);border-radius:12px;padding:20px;box-shadow:0 16px 48px rgba(0,0,0,.2);max-width:400px;width:90%';
  modal.innerHTML = `
    <div style="font-size:14px;font-weight:600;color:var(--colorTextHeading);margin-bottom:4px">Add Member</div>
    <p style="font-size:11px;color:var(--colorTextMuted);margin-bottom:12px">Add a user to <strong>${sanitize(_selectedFolder.name)}</strong>. They must have logged in at least once.</p>
    <div style="margin-bottom:12px">
      <input id="member-search" type="text" placeholder="Search by name or email..." style="width:100%;padding:8px 10px;border:1px solid var(--colorBorder);border-radius:6px;font-size:12px;background:var(--colorBgElevated);color:var(--colorText);outline:none;box-sizing:border-box">
    </div>
    <div id="member-results" style="max-height:200px;overflow-y:auto;margin-bottom:12px">
      ${available.map(u => `
        <div data-add-user="${u._id}" style="display:flex;align-items:center;gap:8px;padding:6px 8px;border-radius:6px;cursor:pointer" onmouseover="this.style.background='var(--colorBgElevated)'" onmouseout="this.style.background='none'">
          <div style="flex:1;min-width:0">
            <div style="font-size:11px;font-weight:500;color:var(--colorText)">${sanitize(u.display_name || u.email)}</div>
            <div style="font-size:9px;color:var(--colorTextMuted)">${sanitize(u.email)}</div>
          </div>
        </div>
      `).join('')}
      ${available.length === 0 ? '<div style="font-size:11px;color:var(--colorTextLight);text-align:center;padding:12px">All users are already members</div>' : ''}
    </div>
    <div style="display:flex;align-items:center;gap:8px">
      <span style="font-size:10px;color:var(--colorTextLight)">Role:</span>
      <select id="member-role" style="font-size:10px;padding:4px 8px;border-radius:4px;border:1px solid var(--colorBorder);background:var(--colorBgElevated);color:var(--colorText)">
        <option value="editor">Editor</option>
        <option value="viewer">Viewer</option>
      </select>
    </div>
  `;

  overlay.appendChild(modal);
  document.body.appendChild(overlay);

  // Search filter
  const searchInput = document.getElementById('member-search') as HTMLInputElement;
  searchInput?.focus();
  searchInput?.addEventListener('input', () => {
    const q = searchInput.value.toLowerCase();
    modal.querySelectorAll('[data-add-user]').forEach(el => {
      const text = el.textContent?.toLowerCase() || '';
      (el as HTMLElement).style.display = text.includes(q) ? '' : 'none';
    });
  });

  // Add user click
  modal.querySelectorAll('[data-add-user]').forEach(el => {
    el.addEventListener('click', async () => {
      const userId = (el as HTMLElement).dataset.addUser!;
      const user = _allUsers.find(u => u._id === userId);
      const role = (document.getElementById('member-role') as HTMLSelectElement).value as 'editor' | 'viewer';
      try {
        await folderService.addMember(_selectedFolder._id, user.email, role);
        overlay.remove();
        await loadMembers(_selectedFolder._id);
        render();
      } catch (err: any) {
        alert(err.message || 'Failed to add member');
      }
    });
  });
}
